import {
  computed, effect,
  inject,
  Injectable,
  signal,
} from '@angular/core';

import {ActivatedRoute} from '@angular/router';

import {
  form,
  pattern,
  required,
  submit,
  validate,
} from '@angular/forms/signals';


import {
  BRIEFING_EXPECTATIONS,
  CONTACT_AVAILABLE_DOMAINS,
  CONTACT_INITIAL_MODEL,
} from '../constants/contact-page.constants';

import type {
  ContactFormModel,
  ContactSubmissionPayload,
  DemoSubmissionPayload,
  Product,
} from '../models/contact-page.model';

import {ContactApiService} from './contact-api.service';
import {SeoService} from '@app/features/request-demo/core/services/seo.service';

const forbiddenDomains = [
  'gmail.com',
  'yahoo.com',
  'hotmail.com',
  'outlook.com',
  'icloud.com',
  'aol.com',
];

@Injectable()
export class ContactPageFacade {
  readonly expectations =
    BRIEFING_EXPECTATIONS;
  readonly availableDomains =
    CONTACT_AVAILABLE_DOMAINS;
  readonly formModel =
    signal<ContactFormModel>({
      ...CONTACT_INITIAL_MODEL,
    });
  readonly isDemoRequest =
    computed(() => {
      return true;
    });
  readonly contactForm = form(
    this.formModel,
    (schema) => {
      /*
       * Name
       */
      required(schema.name, {
        message:
          'contactUsPage.form.validation.required',
      });

      validate(schema.name, ({value}) => {
        const val =
          value()?.trim() || '';

        if (
          val.length > 0 &&
          val.length < 2
        ) {
          return {
            kind: 'minimum-name-length',
            message:
              'contactUsPage.form.validation.nameLength',
          };
        }

        return undefined;
      });

      /*
       * Email
       */
      required(schema.email, {
        message:
          'contactUsPage.form.validation.required',
      });

      validate(schema.email, ({value}) => {
        const val =
          value()?.trim() || '';

        if (!val) {
          return undefined;
        }
        let invalidProviders = ['yahoo', 'icloud', 'live', 'hotmail', 'aol']
        let [email, provider] = val.split('@')
        console.log(email, provider, 'email')

        if (invalidProviders.includes(provider)) {
          return {
            kind: 'email',
            message:
              'contactUsPage.form.validation.email',
          };
        }


        const domain =
          val
            .split('@')[1]
            ?.toLowerCase();

        if (
          domain &&
          forbiddenDomains.includes(domain)
        ) {
          return {
            kind: 'notBusinessEmail',
            message:
              'contactUsPage.form.validation.businessEmail',
          };
        }

        return undefined;
      });

      /*
       * Phone (Optional)
       */
      validate(schema.phone, ({value}) => {
        const val = value()?.trim() || '';
        if (val && !/^[0-9+()\-\s]{7,20}$/.test(val)) {
          return {
            kind: 'phone-pattern',
            message: 'contactUsPage.form.validation.phone',
          };
        }
        return undefined;
      });

      /*
       * Company
       */
      required(schema.company_name, {
        message:
          'contactUsPage.form.validation.required',
      });

      /*
       * Subject
       */
      required(schema.subject, {
        message:
          'contactUsPage.form.validation.required',
      });

      /*
       * Organization type
       */
      required(schema.organization_type, {
        message:
          'contactUsPage.form.validation.required',
      });

      /*
       * Domains
       */
      /*
       * Time frame
       *
       * Demo request does not require time frame.
       */
      if (!this.isDemoRequest()) {
        required(schema.time_frame, {
          message:
            'contactUsPage.form.validation.required',
        });
      }

      /*
       * Message
       */
      required(schema.message, {
        message:
          'contactUsPage.form.validation.required',
      });
    },
    {
      name: 'contact-message',
    },
  );
  readonly isSubmitting =
    computed(() =>
      this.contactForm().submitting(),
    );
  readonly controlsDisabled =
    computed(() =>
      this.contactForm().submitting(),
    );
  private readonly api = inject(ContactApiService);
  private readonly seoService = inject(SeoService);
  private readonly route = inject(ActivatedRoute);
  private readonly productsValidationState =
    signal(false);
  readonly productsValidation =
    this.productsValidationState.asReadonly();
  private readonly submissionErrorState =
    signal<string | null>(null);
  readonly submissionErrorKey =
    this.submissionErrorState.asReadonly();
  private readonly submissionSuccessState =
    signal(false);
  readonly submissionSuccess =
    this.submissionSuccessState.asReadonly();
  private readonly requestReferenceState =
    signal<string | null>(null);
  readonly requestReference =
    this.requestReferenceState.asReadonly();
  private readonly currentStepState =
    signal<number>(1);
  readonly currentStep =
    this.currentStepState.asReadonly();
  private readonly expandedPillarState =
    signal<string | null>(null);
  readonly expandedPillar =
    this.expandedPillarState.asReadonly();
  private readonly productsState =
    signal<Product[]>([]);
  readonly products =
    this.productsState.asReadonly();
  private readonly productsLoadingState =
    signal(false);
  readonly productsLoading =
    this.productsLoadingState.asReadonly();
  private readonly selectedProductIdsState =
    signal<number[]>([]);
  readonly selectedProductIds =
    this.selectedProductIdsState.asReadonly();
  readonly selectedProductsLabel =
    computed(() => {
      const selectedIds =
        this.selectedProductIdsState();

      if (selectedIds.length === 0) {
        return 'Select products';
      }

      const selectedProducts =
        this.productsState().filter(
          (product) =>
            selectedIds.includes(
              Number(product.id),
            ),
        );

      if (selectedProducts.length === 0) {
        return 'Select products';
      }

      if (selectedProducts.length === 1) {
        return this.getProductName(
          selectedProducts[0],
        );
      }

      return `${selectedProducts.length} products selected`;
    });
  private readonly productsDropdownOpenState =
    signal(false);
  readonly productsDropdownOpen =
    this.productsDropdownOpenState.asReadonly();

  constructor() {
    this.seoService.update({
      titleKey: 'seo.contact.title',
      descriptionKey: 'seo.contact.description',
      canonicalPath: '/contact',
    });
    effect(() => {
      console.log(this.isDemoRequest(), 'isDemoRequest')
    });
    if (this.isDemoRequest()) {
      void this.loadProducts();
    }
  }

  /*
   * ================================================================
   * PRODUCTS
   * ================================================================
   */

  toggleProductsDropdown(): void {
    if (
      this.productsLoadingState() ||
      this.productsState().length === 0
    ) {
      return;
    }

    this.productsDropdownOpenState.update(
      (open) => !open,
    );
  }

  closeProductsDropdown(): void {
    this.productsDropdownOpenState.set(false);
  }

  isProductSelected(
    id: number | string,
  ): boolean {


    return this.selectedProductIdsState()
      .includes(id as any);
  }

  toggleProduct(
    id: number | string,
  ): void {
    const productId =
      Number(id);

    if (this.isProductSelected(id)) {
      this.selectedProductIdsState.set(
        [...this.selectedProductIdsState().filter(item => item !== id)]
      );
    } else {
      this.selectedProductIdsState.set(
        [...this.selectedProductIdsState() as any, id]
      );
    }
  }

  clearSelectedProducts(): void {
    this.selectedProductIdsState.set([]);

    this.productsValidationState.set(false);
  }

  getProductName(product: Product): string {
    const productWithPossibleTitle =
      product as Product & {
        [key: string]: unknown;
      };

    const name =
      productWithPossibleTitle['name'];

    const title =
      productWithPossibleTitle['title'];

    if (
      typeof name === 'string' &&
      name.trim()
    ) {
      return name;
    }

    if (
      typeof title === 'string' &&
      title.trim()
    ) {
      return title;
    }

    return `Product #${product.id}`;
  }

  onProductsChange(
    event: Event,
  ): void {
    const select =
      event.target as HTMLSelectElement;

    if (!select) {
      return;
    }

    const selectedIds =
      Array.from(
        select.selectedOptions,
      )
        .map((option) =>
          Number(option.value),
        )
        .filter((id) =>
          Number.isFinite(id),
        );

    this.selectedProductIdsState.set(
      selectedIds,
    );
  }

  togglePillar(id: string): void {
    this.expandedPillarState.set(
      this.expandedPillarState() === id
        ? null
        : id,
    );
  }

  /*
   * ================================================================
   * PILLARS
   * ================================================================
   */

  nextStep(): void {
    const current =
      this.currentStepState();

    if (current === 1) {
      this.touchProfileFields();

      if (this.profileIsInvalid()) {
        this.focusFirstInvalidProfileField();
        return;
      }

      this.currentStepState.set(2);
      return;
    }

    if (current === 2) {
      this.touchContextFields();
      console.log(this.selectedProductIdsState(), 'test');
      if (
        this.selectedProductIdsState().length === 0
      ) {
        this.productsValidationState.set(true);
      } else {
        this.productsValidationState.set(false);
      }

      if (this.contextIsInvalid()) {
        this.focusFirstInvalidContextField();
        return;
      }

      this.currentStepState.set(3);
    }
  }

  /*
   * ================================================================
   * STEPPER
   * ================================================================
   */

  previousStep(): void {
    if (this.currentStepState() > 1) {
      this.productsValidationState.set(false);

      this.currentStepState.update(
        (step) => step - 1,
      );
    }
  }

  /*
   * ================================================================
   * DOMAINS
   * ================================================================
   */

  toggleDomain(
    id: string,
  ): void {

  }

  async submitForm(): Promise<void> {
    this.submissionErrorState.set(null);

    this.submissionSuccessState.set(false);

    if (
      this.isDemoRequest() &&
      this.selectedProductIdsState().length === 0
    ) {
      this.submissionErrorState.set(
        'contactUsPage.form.validation.required',
      );

      return;
    }

    const success = await submit(
      this.contactForm,
      async (field) => {
        try {
          const model =
            field().value();

          /*
           * ========================================================
           * DEMO
           * ========================================================
           */

          if (this.isDemoRequest()) {
            const productIds = [
              ...this.selectedProductIdsState(),
            ];

            if (
              productIds.length === 0
            ) {
              this.submissionErrorState.set(
                'contactUsPage.form.validation.required',
              );

              return {
                kind: 'products-required',
                message:
                  'contactUsPage.form.validation.required',
              };
            }

            const payload:
              DemoSubmissionPayload = {
              name:
                model.name.trim(),

              email:
                model.email.trim(),

              message:
                model.message.trim(),

              product_ids:
              productIds,

              company_name:
                model.company_name.trim(),

              subject:
                model.subject.trim(),

              organization_type:
                model.organization_type.trim(),

            };

            console.log(
              'Submitting DEMO payload:',
              payload,
            );

            const result =
              await this.api.submitDemo(
                payload,
              );

            this.requestReferenceState.set(
              result.requestId,
            );

            this.submissionSuccessState.set(
              true,
            );

            this.productsDropdownOpenState.set(
              false,
            );

            return undefined;
          }

          /*
           * ========================================================
           * NORMAL CONTACT
           * ========================================================
           */

          const payload:
            ContactSubmissionPayload = {
            name:
              model.name.trim(),

            email:
              model.email.trim(),

            phone:
              model.phone.trim(),

            company_name:
              model.company_name.trim(),

            subject:
              model.subject.trim(),

            organization_type:
              model.organization_type.trim(),

            time_frame:
              model.time_frame.trim(),

            message:
              model.message.trim(),
          };

          console.log(
            'Submitting CONTACT payload:',
            payload,
          );

          const result =
            await this.api.submitMessage(
              payload,
            );

          this.requestReferenceState.set(
            result.requestId,
          );

          this.submissionSuccessState.set(
            true,
          );

          return undefined;
        } catch (err) {
          console.error(
            'API Error:',
            err,
          );

          this.submissionErrorState.set(
            'contactUsPage.form.status.submitError',
          );

          return {
            kind: 'server',
            message:
              'contactUsPage.form.status.submitError',
          };
        }
      },
    );

    if (!success) {
      const firstError =
        this.contactForm()
          .errorSummary()[0];

      firstError
        ?.fieldTree()
        .focusBoundControl();

      console.log(
        'Form validation failed:',
        this.contactForm()
          .errorSummary(),
      );
    }
  }

  /*
   * ================================================================
   * SUBMIT
   * ================================================================
   */

  startAnotherMessage(): void {
    this.formModel.set({
      ...CONTACT_INITIAL_MODEL,
    });

    this.selectedProductIdsState.set([]);

    this.productsDropdownOpenState.set(false);

    this.contactForm().reset();

    this.submissionErrorState.set(null);

    this.submissionSuccessState.set(false);

    this.requestReferenceState.set(null);

    this.currentStepState.set(1);
  }

  /*
   * ================================================================
   * RESET
   * ================================================================
   */

  private async loadProducts(): Promise<void> {
    if (this.productsLoadingState()) {
      return;
    }

    this.productsLoadingState.set(true);

    try {
      const products =
        await this.api.getProducts();

      console.log(
        'Products API response:',
        products,
      );

      /*
       * API response:
       *
       * {
       *   success: true,
       *   status: 200,
       *   message: '',
       *   data: [...]
       * }
       *
       * ContactApiService is currently typed as returning
       * Product[], while the actual API response is an object.
       * Cast only here so the rest of the existing logic remains unchanged.
       */
      const response =
        products as unknown as {
          data?: Product[];
          products?: Product[];
        };

      const productList =
        Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.products)
            ? response.products
            : Array.isArray(products)
              ? products
              : [];

      this.productsState.set(
        productList,
      );
    } catch (err) {
      console.error(
        'Products API Error:',
        err,
      );

      this.productsState.set([]);
    } finally {
      this.productsLoadingState.set(false);
    }
  }

  /*
   * ================================================================
   * TOUCH FIELDS
   * ================================================================
   */

  private touchProfileFields(): void {
    this.contactForm
      .name()
      .markAsTouched();

    this.contactForm
      .email()
      .markAsTouched();

    this.contactForm
      .company_name()
      .markAsTouched();

    this.contactForm
      .subject()
      .markAsTouched();

    this.contactForm
      .organization_type()
      .markAsTouched();

    if (!this.isDemoRequest()) {
      this.contactForm
        .phone()
        .markAsTouched();
    }
  }

  private touchContextFields(): void {
    // this.contactForm
    //   .domains()
    //   .markAsTouched();

    this.contactForm
      .message()
      .markAsTouched();

    if (!this.isDemoRequest()) {
      this.contactForm
        .time_frame()
        .markAsTouched();
    }
  }

  /*
   * ================================================================
   * VALIDATION
   * ================================================================
   */

  private profileIsInvalid(): boolean {
    const commonInvalid =
      this.contactForm
        .name()
        .invalid() ||

      this.contactForm
        .email()
        .invalid() ||

      this.contactForm
        .company_name()
        .invalid() ||

      this.contactForm
        .subject()
        .invalid() ||

      this.contactForm
        .organization_type()
        .invalid();

    if (this.isDemoRequest()) {
      return commonInvalid;
    }

    return (
      commonInvalid ||
      this.contactForm
        .phone()
        .invalid()
    );
  }

  private contextIsInvalid(): boolean {
    // const commonInvalid =
    //   this.contactForm
    //     .domains()
    //     .invalid() ||
    //
    //   this.contactForm
    //     .message()
    //     .invalid();
    //
    // if (this.isDemoRequest()) {
    //   return (
    //     commonInvalid ||
    //     this.selectedProductIdsState()
    //       .length === 0
    //   );
    // }
    //
    // return (
    //   commonInvalid ||
    //   this.contactForm
    //     .time_frame()
    //     .invalid()
    // );
    return false
  }

  /*
   * ================================================================
   * FOCUS PROFILE
   * ================================================================
   */

  private focusFirstInvalidProfileField(): void {
    if (
      this.contactForm
        .name()
        .invalid()
    ) {
      this.contactForm
        .name()
        .focusBoundControl();

      return;
    }

    if (
      this.contactForm
        .email()
        .invalid()
    ) {
      this.contactForm
        .email()
        .focusBoundControl();

      return;
    }

    if (
      !this.isDemoRequest() &&
      this.contactForm
        .phone()
        .invalid()
    ) {
      this.contactForm
        .phone()
        .focusBoundControl();

      return;
    }

    if (
      this.contactForm
        .company_name()
        .invalid()
    ) {
      this.contactForm
        .company_name()
        .focusBoundControl();

      return;
    }

    if (
      this.contactForm
        .subject()
        .invalid()
    ) {
      this.contactForm
        .subject()
        .focusBoundControl();

      return;
    }

    if (
      this.contactForm
        .organization_type()
        .invalid()
    ) {
      this.contactForm
        .organization_type()
        .focusBoundControl();
    }
  }

  /*
   * ================================================================
   * FOCUS CONTEXT
   * ================================================================
   */

  private focusFirstInvalidContextField(): void {
    // if (
    //   this.contactForm
    //     .domains()
    //     .invalid()
    // ) {
    //   this.contactForm
    //     .domains()
    //     .focusBoundControl();
    //
    //   return;
    // }

    if (
      this.isDemoRequest() &&
      this.selectedProductIdsState()
        .length === 0
    ) {
      return;
    }

    if (
      !this.isDemoRequest() &&
      this.contactForm
        .time_frame()
        .invalid()
    ) {
      this.contactForm
        .time_frame()
        .focusBoundControl();

      return;
    }

    if (
      this.contactForm
        .message()
        .invalid()
    ) {
      this.contactForm
        .message()
        .focusBoundControl();
    }
  }
}
